import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx'; // Asegúrate de instalar la librería: npm install xlsx
import './FluidezLectora.css';

const levelOptions = [
  '1A','1B','2A','2B','3A','3B','4A','4B','5A','5B','6A','6B','7A','7B','8A','8B','IA','IB','IIA','IIB'
];

const readingFields = [
  'reading_eval1',
  'reading_eval2',
  'reading_eval3',
  'reading_eval4'
];

const FluidezLectora = () => {
  const [filterYear, setFilterYear] = useState('');
  const [filterNivel, setFilterNivel] = useState('');
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');
  
  // Referencia para el input file (para importar CSV)
  const fileInputRef = useRef(null);

  const getValue = (value) => {
    if (value === null || value === undefined) return null;
    const str = String(value).trim();
    return str === '' ? null : Number(str);
  };

  const fetchStudents = async () => {
    if (!filterYear || !filterNivel) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `https://asistencia-betterland.onrender.com/fluidez/estudiantes?yearFluidez=${filterYear}&nivel=${filterNivel}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        data.sort((a, b) => a.lastnamepaternal.localeCompare(b.lastnamepaternal));
        const modifiedData = data.map(student => ({
          ...student,
          reading_eval1: student.reading_eval1 ?? '',
          reading_eval2: student.reading_eval2 ?? '',
          reading_eval3: student.reading_eval3 ?? '',
          reading_eval4: student.reading_eval4 ?? ''
        }));
        setStudents(modifiedData);
      } else {
        setStudents([]);
      }
    } catch (error) {
      console.error("Error al cargar estudiantes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e, index, field) => {
    const newStudents = [...students];
    newStudents[index][field] = e.target.value;
    setStudents(newStudents);
  };

  const handleKeyDown = (e, rowIndex, field) => {
    if (!['ArrowUp','ArrowDown','ArrowLeft','ArrowRight'].includes(e.key)) return;
    e.preventDefault();
    const currentFieldIndex = readingFields.indexOf(field);
    let targetRow = rowIndex;
    let targetFieldIndex = currentFieldIndex;
    switch(e.key) {
      case 'ArrowRight':
        if (currentFieldIndex < readingFields.length - 1) targetFieldIndex++;
        break;
      case 'ArrowLeft':
        if (currentFieldIndex > 0) targetFieldIndex--;
        break;
      case 'ArrowUp':
        if (rowIndex > 0) targetRow--;
        break;
      case 'ArrowDown':
        if (rowIndex < students.length - 1) targetRow++;
        break;
    }
    const targetField = readingFields[targetFieldIndex];
    const targetInput = document.getElementById(`input-${targetRow}-${targetField}`);
    targetInput?.focus();
  };

  const handleSave = async (student) => {
    try {
      const token = localStorage.getItem('token');
      const body = {
        yearFluidez: filterYear,
        reading_eval1: getValue(student.reading_eval1),
        reading_eval2: getValue(student.reading_eval2),
        reading_eval3: getValue(student.reading_eval3),
        reading_eval4: getValue(student.reading_eval4)
      };
      const response = await fetch(
        `https://asistencia-betterland.onrender.com/fluidez/estudiantes/${student.rut}/evaluaciones/reading`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(body)
        }
      );
      if (response.ok) {
        setUpdateMessage(`Evaluaciones actualizadas para ${student.firstname} ${student.lastnamepaternal}`);
        fetchStudents();
      } else {
        setUpdateMessage("Error al actualizar evaluaciones.");
      }
    } catch (error) {
      console.error("Error al actualizar:", error);
      setUpdateMessage("Error al conectar con el servidor.");
    }
  };

  const handleSaveAll = async () => {
    try {
      const token = localStorage.getItem('token');
      const promises = students.map(student => {
        const body = {
          yearFluidez: filterYear,
          reading_eval1: getValue(student.reading_eval1),
          reading_eval2: getValue(student.reading_eval2),
          reading_eval3: getValue(student.reading_eval3),
          reading_eval4: getValue(student.reading_eval4)
        };
        return fetch(
          `https://asistencia-betterland.onrender.com/fluidez/estudiantes/${student.rut}/evaluaciones/reading`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(body)
          }
        );
      });
      const results = await Promise.all(promises);
      if (results.every(r => r.ok)) {
        setUpdateMessage("Evaluaciones actualizadas para todos los estudiantes.");
        fetchStudents();
      } else {
        setUpdateMessage("Error al actualizar algunas evaluaciones.");
      }
    } catch (error) {
      console.error("Error al actualizar en lote:", error);
      setUpdateMessage("Error al conectar con el servidor para guardar todo.");
    }
  };

  const exportCSV = () => {
    if (!students.length) return;
    const headers = [
      "Nivel","RUT","Apellido Paterno","Apellido Materno","Nombres",
      "Fluidez Lectora 1","Fluidez Lectora 2","Fluidez Lectora 3","Fluidez Lectora 4"
    ];
    const rows = students.map(stu => [
      stu.nivel,stu.rut,stu.lastnamepaternal,stu.lastnamematernal,stu.firstname,
      stu.reading_eval1,stu.reading_eval2,stu.reading_eval3,stu.reading_eval4
    ]);
    const csvContent = "\uFEFF" + headers.join(";") + "\n"
      + rows.map(r => r.join(";")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `fluidez_lectora_${filterYear}_${filterNivel}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ({ target }) => {
      const text = target.result.replace(/^\uFEFF/, '');
      const lines = text.split("\n").filter(l => l.trim());
      const headers = lines[0].split(";").map(h => h.trim());
      const data = lines.slice(1).map(line => {
        const vals = line.split(";").map(v => v.trim());
        const obj = {};
        headers.forEach((h,i) => obj[h] = vals[i]);
        return obj;
      });
      const imported = data.map(item => ({
        nivel: item["Nivel"],
        rut: item["RUT"],
        lastnamepaternal: item["Apellido Paterno"],
        lastnamematernal: item["Apellido Materno"],
        firstname: item["Nombres"],
        reading_eval1: item["Fluidez Lectora 1"],
        reading_eval2: item["Fluidez Lectora 2"],
        reading_eval3: item["Fluidez Lectora 3"],
        reading_eval4: item["Fluidez Lectora 4"]
      }));
      setStudents(imported);
    };
    reader.readAsText(file, "UTF-8");
  };
  
  // --- NUEVA FUNCIONALIDAD PARA EXPORTAR TODOS LOS NIVELES A EXCEL CON PESTAÑAS ---

  // Función auxiliar para obtener los datos de un nivel
  const fetchDataForLevel = async (level) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(
        `https://asistencia-betterland.onrender.com/fluidez/estudiantes?yearFluidez=${filterYear}&nivel=${level}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        data.sort((a, b) => {
          if (a.lastnamepaternal < b.lastnamepaternal) return -1;
          if (a.lastnamepaternal > b.lastnamepaternal) return 1;
          return 0;
        });
        const modifiedData = data.map(student => ({
          ...student,
          reading_eval1: student.reading_eval1 === null ? '' : student.reading_eval1,
          reading_eval2: student.reading_eval2 === null ? '' : student.reading_eval2,
          reading_eval3: student.reading_eval3 === null ? '' : student.reading_eval3,
          reading_eval4: student.reading_eval4 === null ? '' : student.reading_eval4
        }));
        return modifiedData;
      } else {
        return [];
      }
    } catch (error) {
      console.error(`Error al cargar datos para el nivel ${level}:`, error);
      return [];
    }
  };

  // Función auxiliar para armar los datos (cabeceras y filas) para cada hoja
  const createSheetData = (level, studentsData) => {
    const headers = [
      "Nivel", "RUT", "Apellido Paterno", "Apellido Materno", "Nombres",
      "Fluidez Lectora 1", "Fluidez Lectora 2", "Fluidez Lectora 3", "Fluidez Lectora 4"
    ];
    const rows = studentsData.map(stu => [
      stu.nivel,
      stu.rut,
      stu.lastnamepaternal,
      stu.lastnamematernal,
      stu.firstname,
      stu.reading_eval1,
      stu.reading_eval2,
      stu.reading_eval3,
      stu.reading_eval4
    ]);
    return [headers, ...rows];
  };

  // Función para exportar todos los niveles a un archivo Excel con pestañas en el orden definido
  const exportAllExcel = async () => {
    if (!filterYear) {
      alert("Por favor, seleccione un año para exportar.");
      return;
    }

    setLoading(true);
    const workbook = XLSX.utils.book_new();
    let todosLosEstudiantes = []; // Acumulador para la hoja extra

    // 1. Obtenemos en paralelo los datos para cada nivel
    const sheetsData = await Promise.all(
      levelOptions.map(async (level) => {
        const dataForLevel = await fetchDataForLevel(level);
        
        // Si hay datos, los sumamos a nuestra lista global
        if (dataForLevel && dataForLevel.length > 0) {
          todosLosEstudiantes = [...todosLosEstudiantes, ...dataForLevel];
        }

        const sheetData = dataForLevel.length > 0
          ? createSheetData(level, dataForLevel)
          : [["No hay datos para este nivel"]];
        return { level, sheetData };
      })
    );

    // 2. CREAR LA HOJA EXTRA (CONSOLIDADO)
    // Ordenamos a todos por apellido paterno
    todosLosEstudiantes.sort((a, b) => a.lastnamepaternal.localeCompare(b.lastnamepaternal));
    
    const globalSheetData = createSheetData("TODOS", todosLosEstudiantes);
    const globalWorksheet = XLSX.utils.aoa_to_sheet(globalSheetData);
    
    // Añadimos la hoja de "TODOS" al principio del libro
    XLSX.utils.book_append_sheet(workbook, globalWorksheet, "CONSOLIDADO_TOTAL");

    // 3. Se recorren los niveles para agregar cada hoja individual (como estaba antes)
    levelOptions.forEach(level => {
      const sheetDataObj = sheetsData.find(sheet => sheet.level === level);
      const worksheet = XLSX.utils.aoa_to_sheet(sheetDataObj.sheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, level);
    });
    
    XLSX.writeFile(workbook, `fluidez_lectora_${filterYear}_completo.xlsx`);
    setLoading(false);
  };

  useEffect(() => {
    fetchStudents();
  }, [filterYear, filterNivel]);

  return (
    <>
      {/* 1) Solo el título dentro del contenedor fijo */}
      <div className="fluidez-container">
        <h2 className="fluidez-title">Fluidez Lectora</h2>
      </div>

      {/* 2) Barra de filtros fuera del fixed */}
      <div className="fluidez-controls">
        <div className="filter">
          <label>Año:</label>
          <select value={filterYear} onChange={e => setFilterYear(e.target.value)}>
            <option value="">Seleccione año</option>
            <option value="2026">2026</option>
            <option value="2025">2025</option>
          </select>
        </div>
        <div className="filter">
          <label>Nivel:</label>
          <select value={filterNivel} onChange={e => setFilterNivel(e.target.value)}>
            <option value="">Seleccione nivel</option>
            {levelOptions.map(l => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>
        <button className="create-button" onClick={fetchStudents}>Buscar</button>
        <button className="create-button" onClick={exportAllExcel}>Exportar Todo</button>
      </div>

      {/* 3) Contenido principal (tarjetas y tabla) también fuera del fixed */}
      <div className="fluidez-content">
        {loading ? (
          <p>Cargando estudiantes…</p>
        ) : students.length > 0 ? (
          <>
              <div className="table-header">
                <h3>
                  {filterNivel
                    ? `Lista de Estudiantes del ${filterNivel}`
                    : 'Lista de Estudiantes'}
                </h3>
                <button className="create-button" onClick={handleSaveAll}>
                  Guardar Todo
                </button>
              </div>
              {updateMessage && <p className="message">{updateMessage}</p>}
              <table className="fluidez-table">
                <thead>
                  <tr>
                    <th>Nivel</th>
                    <th>RUT</th>
                    <th>Apellido P.</th>
                    <th>Apellido M.</th>
                    <th>Nombres</th>
                    <th>FL 1</th>
                    <th>FL 2</th>
                    <th>FL 3</th>
                    <th>FL 4</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((stu, i) => (
                    <tr key={stu.rut}>
                      <td>{stu.nivel}</td>
                      <td>{stu.rut}</td>
                      <td>{stu.lastnamepaternal}</td>
                      <td>{stu.lastnamematernal}</td>
                      <td>{stu.firstname}</td>
                      {readingFields.map(f => (
                        <td key={f}>
                          <input
                            id={`input-${i}-${f}`}
                            type="number"
                            value={stu[f]}
                            onChange={e => handleInputChange(e, i, f)}
                            onKeyDown={e => handleKeyDown(e, i, f)}
                            style={{ width: '50px', fontSize: '0.8rem' }}
                          />
                        </td>
                      ))}
                      <td>
                        <button
                          className="create-button"
                          onClick={() => handleSave(stu)}
                        >
                          Guardar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>


            {/* Export/Import CSV */}
            <div className="csv-actions">
              <button className="create-button" onClick={exportCSV}>
                Exportar CSV
              </button>
              <button
                className="create-button"
                onClick={() => fileInputRef.current.click()}
              >
                Importar CSV
              </button>
              <input
                type="file"
                accept=".csv"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleImportCSV}
              />
            </div>
          </>
        ) : (
          <p>No hay datos para mostrar.</p>
        )}
      </div>
    </>
  );
};

export default FluidezLectora;